import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import CTABand from "@/components/sections/CTABand";

const VERBS = [
  { verb: "Detect", desc: "Identify emerging patient signals." },
  { verb: "Validate", desc: "Assess relevance and consistency across evidence sources." },
  { verb: "Translate", desc: "Convert patient insights into evidence-ready narratives." },
  {
    verb: "Activate",
    desc: "Support decisions across Medical Affairs, HEOR, Market Access, Patient Engagement, and Brand teams.",
  },
];

const TEAM_OUTPUTS = [
  {
    title: "Patient Experience Insights",
    desc: "A structured view of patient priorities, unmet needs, and treatment experience.",
  },
  {
    title: "Evidence Narratives",
    desc: "Patient-centered evidence translated into decision-ready language.",
  },
  {
    title: "Evidence Gap Assessment",
    desc: "Identification of areas where additional evidence may strengthen strategy.",
  },
  {
    title: "Strategic Recommendations",
    desc: "Actionable guidance for Medical Affairs, Market Access, HEOR, Patient Engagement, and Brand teams.",
  },
];

export default function Framework() {
  return (
    <div data-testid="page-framework">
      {/* Hero */}
      <section className="bg-ink-0 relative overflow-hidden" data-testid="framework-hero">
        <div className="absolute inset-0 glow-rust opacity-80 pointer-events-none" aria-hidden="true" />
        <div className="absolute inset-0 grid-faint opacity-50 pointer-events-none" aria-hidden="true" />
        <div className="container-edge pt-20 lg:pt-28 pb-20 lg:pb-24 relative z-10">
          <div className="grid grid-cols-12 gap-8">
            <Reveal className="col-span-12 lg:col-span-9">
              <div className="eyebrow">The framework</div>
              <h1
                className="font-serif font-normal text-5xl md:text-6xl lg:text-[6rem] leading-[1.02] tracking-tight mt-4 text-parchment-bright"
                data-testid="framework-headline"
              >
                PentaVoice<sup className="text-[0.35em] align-super ml-1 text-rust">™</sup>
              </h1>
              <p className="mt-6 font-serif italic text-2xl md:text-[1.75rem] text-parchment/85 leading-snug max-w-3xl">
                Patient voice. Translated into evidence.
              </p>
              <p className="mt-8 text-parchment/80 text-lg leading-relaxed max-w-3xl">
                PentaVoice<sup className="text-[0.55em] align-super">™</sup> is the framework behind Voice2Evidence.
              </p>
              <p className="mt-5 text-parchment/75 text-[16.5px] leading-relaxed max-w-3xl">
                It is designed to transform fragmented patient experience signals into structured, evidence-ready insights that support launch readiness, Medical Affairs, Market Access, HEOR, Patient Engagement, and brand strategy.
              </p>
              <p className="mt-10 font-mono text-[13px] uppercase tracking-[0.22em] text-rust">
                Voice → Evidence → Strategy
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Why PentaVoice */}
      <section
        className="bg-ink-2 relative border-t border-white/[0.06]"
        data-testid="framework-why-pentavoice"
      >
        <div className="container-edge py-24 lg:py-32">
          <div className="grid grid-cols-12 gap-x-8 gap-y-10">
            <Reveal className="col-span-12 lg:col-span-5">
              <div className="eyebrow">Why PentaVoice<sup className="text-[0.55em] align-super">™</sup></div>
              <h2 className="font-serif font-normal text-3xl md:text-5xl lg:text-[3.5rem] tracking-tight mt-4 text-parchment-bright leading-[1.05]">
                Patient voice rarely exists in one place.
              </h2>
            </Reveal>

            <Reveal className="col-span-12 lg:col-span-7 lg:pt-2" delay={120}>
              <p className="text-parchment/80 text-lg leading-relaxed max-w-xl">
                Patients express their experiences across multiple channels, evidence sources, and healthcare interactions. Looking at a single source often creates an incomplete picture.
              </p>
              <p className="mt-5 text-parchment/75 text-[15.5px] leading-relaxed max-w-xl">
                PentaVoice<sup className="text-[0.55em] align-super">™</sup> brings together multiple streams of patient-relevant evidence to create a more complete understanding of unmet needs, treatment experience, quality of life, and patient priorities.
              </p>
              <div className="mt-9 pt-8 border-t border-white/[0.10] max-w-xl">
                <p className="font-serif text-2xl text-parchment-bright leading-snug">
                  The result is not more data.
                </p>
                <p className="font-serif text-2xl text-parchment-bright italic leading-snug mt-2">
                  The result is greater confidence in decision-making.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Why Triangulation Matters */}
      <section
        className="bg-ink-1 relative border-t border-white/[0.06]"
        data-testid="framework-triangulation"
      >
        <div className="container-edge py-24 lg:py-32">
          <Reveal>
            <div className="eyebrow">Why triangulation matters</div>
            <h2 className="font-serif font-normal text-3xl md:text-5xl lg:text-[3.5rem] tracking-tight mt-4 text-parchment-bright max-w-4xl leading-[1.05]">
              Social listening can identify signals.
              <span className="block italic text-parchment">Evidence requires validation.</span>
            </h2>
            <p className="mt-7 max-w-3xl text-parchment/75 text-lg leading-relaxed">
              Voice2Evidence combines multiple evidence perspectives to distinguish meaningful patient insights from isolated observations, helping teams focus on what is most relevant for launch, access, medical, and engagement decisions.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.08] border border-white/[0.08]">
            {VERBS.map((v, i) => (
              <Reveal
                key={v.verb}
                delay={i * 70}
                className="bg-ink-2 p-7 card-dark-hover flex flex-col"
                data-testid={`framework-verb-${v.verb.toLowerCase()}`}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rust mb-4">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-serif text-[1.6rem] text-parchment-bright leading-snug">
                  {v.verb}
                </h3>
                <p className="mt-4 text-[14px] text-parchment/70 leading-relaxed flex-1">{v.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* What Teams Receive */}
      <section
        className="bg-ink-2 relative border-t border-white/[0.06]"
        data-testid="framework-what-teams-receive"
      >
        <div className="container-edge py-24 lg:py-32">
          <Reveal>
            <div className="eyebrow">What teams receive</div>
            <h2 className="font-serif font-normal text-3xl md:text-5xl lg:text-[3.5rem] tracking-tight mt-4 text-parchment-bright max-w-4xl leading-[1.05]">
              Four outputs. One coherent engagement.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.08] border border-white/[0.08]">
            {TEAM_OUTPUTS.map((o, i) => (
              <Reveal
                key={o.title}
                delay={i * 80}
                className="bg-ink-1 p-8 lg:p-10 card-dark-hover flex flex-col"
                data-testid={`framework-output-${o.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rust mb-4">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-serif text-[1.6rem] text-parchment-bright leading-snug">
                  {o.title}
                </h3>
                <p className="mt-4 text-[14.5px] text-parchment/70 leading-relaxed flex-1">{o.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Built on Established Evidence Principles */}
      <section
        className="bg-ink-1 relative border-t border-white/[0.06]"
        data-testid="framework-principles"
      >
        <div className="container-edge py-24 lg:py-32">
          <div className="grid grid-cols-12 gap-x-8 gap-y-10">
            <Reveal className="col-span-12 lg:col-span-5">
              <div className="eyebrow">Built on established evidence principles</div>
              <h2 className="font-serif font-normal text-3xl md:text-5xl lg:text-[3.5rem] tracking-tight mt-4 text-parchment-bright leading-[1.05]">
                Methodologically anchored. Rigour on the record.
              </h2>
            </Reveal>

            <Reveal className="col-span-12 lg:col-span-7 lg:pt-2" delay={120}>
              <p className="text-parchment/80 text-lg leading-relaxed max-w-xl">
                Voice2Evidence is informed by established approaches across patient-focused drug development, patient experience evidence, PRO methodology, real-world evidence, and health technology assessment.
              </p>
              <p className="mt-5 text-parchment/75 text-[15.5px] leading-relaxed max-w-xl">
                Every engagement incorporates evidence triangulation, expert validation, and transparent interpretation of patient-relevant insights.
              </p>
              <p className="mt-10 text-parchment/55 text-[12.5px] italic leading-relaxed max-w-xl">
                References to external frameworks are provided for methodological orientation only and do not imply endorsement, certification, approval, or formal affiliation.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
